import React from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';

const PersonalInfoTab = ({ formData, errors, onChange, onValidate }) => {
  const salutationOptions = [
    { value: 'herr', label: 'Herr' },
    { value: 'frau', label: 'Frau' },
    { value: 'divers', label: 'Divers' }
  ];

  const nationalityOptions = [
    { value: 'de', label: 'Deutschland' },
    { value: 'at', label: 'Österreich' },
    { value: 'ch', label: 'Schweiz' },
    { value: 'pl', label: 'Polen' },
    { value: 'tr', label: 'Türkei' },
    { value: 'it', label: 'Italien' },
    { value: 'ro', label: 'Rumänien' },
    { value: 'other', label: 'Andere' }
  ];

  const maritalStatusOptions = [
    { value: 'ledig', label: 'Ledig' },
    { value: 'verheiratet', label: 'Verheiratet' },
    { value: 'geschieden', label: 'Geschieden' },
    { value: 'verwitwet', label: 'Verwitwet' },
    { value: 'getrennt', label: 'Getrennt lebend' }
  ];

  const handleInputChange = (field, value) => {
    onChange(field, value);
    if (onValidate) {
      onValidate(field, value);
    }
  };

  return (
    <div className="space-y-6">
      {/* Identification Section */}
      <div className="bg-muted/30 rounded-lg p-6">
        <div className="flex items-center gap-2 mb-4">
          <Icon name="IdCard" size={20} className="text-primary" />
          <h3 className="text-lg font-semibold text-foreground">Identifikation</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <Select
            label="Anrede"
            required
            options={salutationOptions}
            value={formData?.salutation}
            onChange={(value) => handleInputChange('salutation', value)}
            error={errors?.salutation}
            placeholder="Bitte wählen"
          />

          <Input
            label="Vorname"
            type="text"
            required
            value={formData?.firstName}
            onChange={(e) => handleInputChange('firstName', e?.target?.value)}
            error={errors?.firstName}
            placeholder="Max"
            maxLength={50}
          />

          <Input
            label="Nachname"
            type="text"
            required
            value={formData?.lastName}
            onChange={(e) => handleInputChange('lastName', e?.target?.value)}
            error={errors?.lastName}
            placeholder="Mustermann"
            maxLength={50}
          />

          <Input
            label="Geburtsname"
            type="text"
            value={formData?.birthName}
            onChange={(e) => handleInputChange('birthName', e?.target?.value)}
            placeholder="Optional"
            maxLength={50}
          />

          <Input
            label="Geburtsdatum"
            type="date"
            required
            value={formData?.dateOfBirth}
            onChange={(e) => handleInputChange('dateOfBirth', e?.target?.value)}
            error={errors?.dateOfBirth}
            max={new Date()?.toISOString()?.split('T')?.[0]}
          />

          <Input
            label="Geburtsort"
            type="text"
            required
            value={formData?.placeOfBirth}
            onChange={(e) => handleInputChange('placeOfBirth', e?.target?.value)}
            error={errors?.placeOfBirth}
            placeholder="Berlin"
            maxLength={100}
          />

          <Select
            label="Staatsangehörigkeit"
            required
            searchable
            options={nationalityOptions}
            value={formData?.nationality}
            onChange={(value) => handleInputChange('nationality', value)}
            error={errors?.nationality}
            placeholder="Bitte wählen"
          />

          <Input
            label="Personalausweisnummer"
            type="text"
            required
            value={formData?.idNumber}
            onChange={(e) => handleInputChange('idNumber', e?.target?.value)}
            error={errors?.idNumber}
            placeholder="L01X00T471"
            maxLength={10}
            pattern="[A-Z0-9]{10}"
          />

          <Input
            label="Sozialversicherungsnummer"
            type="text"
            required
            value={formData?.socialSecurityNumber}
            onChange={(e) => handleInputChange('socialSecurityNumber', e?.target?.value)}
            error={errors?.socialSecurityNumber}
            placeholder="12 345678 A 123"
            maxLength={12}
          />
        </div>
      </div>
      {/* Personal Details Section */}
      <div className="bg-muted/30 rounded-lg p-6">
        <div className="flex items-center gap-2 mb-4">
          <Icon name="User" size={20} className="text-primary" />
          <h3 className="text-lg font-semibold text-foreground">Persönliche Angaben</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <Select
            label="Familienstand"
            required
            options={maritalStatusOptions}
            value={formData?.maritalStatus}
            onChange={(value) => handleInputChange('maritalStatus', value)}
            error={errors?.maritalStatus}
            placeholder="Bitte wählen"
          />

          <Input
            label="Anzahl Kinder"
            type="number"
            value={formData?.numberOfChildren}
            onChange={(e) => handleInputChange('numberOfChildren', e?.target?.value)}
            placeholder="0"
            min="0"
            max="20"
          />

          <Input
            label="Steueridentifikationsnummer"
            type="text"
            value={formData?.taxId}
            onChange={(e) => handleInputChange('taxId', e?.target?.value)}
            placeholder="12 345 678 901"
            maxLength={11}
          />
        </div>
      </div>
      {/* Address Section */}
      <div className="bg-muted/30 rounded-lg p-6">
        <div className="flex items-center gap-2 mb-4">
          <Icon name="MapPin" size={20} className="text-primary" />
          <h3 className="text-lg font-semibold text-foreground">Wohnanschrift</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2">
            <Input
              label="Straße und Hausnummer"
              type="text"
              required
              value={formData?.street}
              onChange={(e) => handleInputChange('street', e?.target?.value)}
              error={errors?.street}
              placeholder="Musterstraße 123"
              maxLength={100}
            />
          </div>

          <Input
            label="Postleitzahl"
            type="text"
            required
            value={formData?.postalCode}
            onChange={(e) => handleInputChange('postalCode', e?.target?.value)}
            error={errors?.postalCode}
            placeholder="10115"
            maxLength={5}
            pattern="[0-9]{5}"
          />

          <Input
            label="Stadt"
            type="text"
            required
            value={formData?.city}
            onChange={(e) => handleInputChange('city', e?.target?.value)}
            error={errors?.city}
            placeholder="Berlin"
            maxLength={100}
          />

          <Input
            label="Bundesland"
            type="text"
            required
            value={formData?.state}
            onChange={(e) => handleInputChange('state', e?.target?.value)}
            error={errors?.state}
            placeholder="Berlin"
            maxLength={50}
          />

          <Input
            label="Zusätzliche Adressangaben"
            type="text"
            value={formData?.addressSupplement}
            onChange={(e) => handleInputChange('addressSupplement', e?.target?.value)}
            placeholder="3. Stock, Hinterhaus"
            maxLength={100}
          />
        </div>
      </div>
    </div>
  );
};

export default PersonalInfoTab;